import AddIcon from '@mui/icons-material/Add'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { useState } from 'react'
import {
  useCreateMaterial,
  useDeleteMaterial,
  useMaterialCategories,
  useMaterials,
  useUoms,
  useUpdateMaterial,
} from '../api/hooks'
import type { Material, MaterialRequest } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ActiveChip } from '../components/StatusChip'

export function MaterialsPage() {
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Material | null>(null)

  const categories = useMaterialCategories()
  const query = useMaterials({ q: search || undefined, categoryId: categoryId || undefined, page, size })
  const deleteMaterial = useDeleteMaterial()

  return (
    <>
      <PageHeader
        title="Material master"
        subtitle="Codes, units and stock levels used by material receipts and next-day planning."
        actions={
          can('MASTER_MATERIAL:CREATE') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New material
            </Button>
          )
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search name, code or brand"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          label="Category"
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All categories</MenuItem>
          {(categories.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {query.isLoading && <LoadingBlock />}
      {query.error && <ErrorBlock error={query.error} />}

      {query.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell align="right">Min stock</TableCell>
                <TableCell align="right">Max stock</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.content.map((material) => (
                <TableRow key={material.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{material.code}</TableCell>
                  <TableCell>{material.name}</TableCell>
                  <TableCell>{material.categoryName ?? '-'}</TableCell>
                  <TableCell>{material.uomCode ?? '-'}</TableCell>
                  <TableCell>{material.brand ?? '-'}</TableCell>
                  <TableCell align="right">{material.minStock ?? '-'}</TableCell>
                  <TableCell align="right">{material.maxStock ?? '-'}</TableCell>
                  <TableCell>
                    <ActiveChip active={material.active} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {can('MASTER_MATERIAL:UPDATE') && (
                      <Tooltip title="Edit material">
                        <IconButton size="small" onClick={() => setEditing(material)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {can('MASTER_MATERIAL:DELETE') && material.active && (
                      <Tooltip title="Retire material">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Retire "${material.name}"? Past receipts keep it; it leaves the pickers.`,
                              )
                            ) {
                              deleteMaterial.mutate(material.id)
                            }
                          }}
                        >
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {query.data.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No materials yet. The 14 seeded categories are ready to classify them.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={query.data.page.totalElements}
            page={query.data.page.number}
            rowsPerPage={query.data.page.size}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <MaterialDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <MaterialDialog
        open={Boolean(editing)}
        material={editing ?? undefined}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

/** One dialog for both: with a material it edits, without one it creates. */
function MaterialDialog({
  open,
  material,
  onClose,
}: {
  open: boolean
  material?: Material
  onClose: () => void
}) {
  const categories = useMaterialCategories()
  const uoms = useUoms()
  const createMaterial = useCreateMaterial()
  const updateMaterial = useUpdateMaterial()
  const [form, setForm] = useState<MaterialRequest>({ code: '', name: '', categoryId: '', uomId: '' })

  const [loadedId, setLoadedId] = useState<string | null>(null)
  if (open && material && loadedId !== material.id) {
    setLoadedId(material.id)
    setForm({
      code: material.code,
      name: material.name,
      categoryId: material.categoryId,
      uomId: material.uomId,
      brand: material.brand ?? '',
      specification: material.specification ?? '',
      minStock: material.minStock ?? null,
      maxStock: material.maxStock ?? null,
    })
  }

  const saving = createMaterial.isPending || updateMaterial.isPending
  const error = material ? updateMaterial.error : createMaterial.error

  function update<K extends keyof MaterialRequest>(key: K, value: MaterialRequest[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    const body: MaterialRequest = {
      ...form,
      code: form.code.trim(),
      name: form.name.trim(),
      minStock: form.minStock ?? null,
      maxStock: form.maxStock ?? null,
    }
    try {
      if (material) {
        await updateMaterial.mutateAsync({ id: material.id, body })
        setLoadedId(null)
      } else {
        await createMaterial.mutateAsync(body)
        setForm({ code: '', name: '', categoryId: '', uomId: '' })
      }
      onClose()
    } catch {
      // Rendered from the mutation state.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{material ? `Edit ${material.code}` : 'New material'}</DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock
              error={error}
              title={material ? 'Could not save the material' : 'Could not create the material'}
            />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          <TextField
            label="Code"
            required
            value={form.code}
            disabled={Boolean(material)}
            helperText={material ? 'Set once and not editable' : ' '}
            onChange={(e) => update('code', e.target.value)}
          />
          <TextField label="Brand" value={form.brand ?? ''} onChange={(e) => update('brand', e.target.value)} />
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
          <TextField
            select
            label="Category"
            required
            value={form.categoryId}
            onChange={(e) => update('categoryId', e.target.value)}
          >
            {(categories.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Unit of measure"
            required
            value={form.uomId}
            onChange={(e) => update('uomId', e.target.value)}
          >
            {(uoms.data ?? []).map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.code} - {u.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Minimum stock"
            type="number"
            value={form.minStock ?? ''}
            onChange={(e) => update('minStock', e.target.value === '' ? null : Number(e.target.value))}
          />
          <TextField
            label="Maximum stock"
            type="number"
            value={form.maxStock ?? ''}
            onChange={(e) => update('maxStock', e.target.value === '' ? null : Number(e.target.value))}
          />
          <TextField
            label="Specification"
            value={form.specification ?? ''}
            onChange={(e) => update('specification', e.target.value)}
            multiline
            minRows={2}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.code.trim() || !form.name.trim() || !form.categoryId || !form.uomId}
        >
          {saving ? 'Saving...' : material ? 'Save changes' : 'Create material'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
